import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { Button, DataTable, SelectField, Tab, Tabs } from "components";
import {
  useGetAllCustomerUsers,
  useGetLevelMappedCustomers,
  useGetLevelMappedDevices,
  useGetLevelDevices,
  useMapLevelDevices,
  useGlobalStore,
} from "controller";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import {
  LevelDevice,
  LevelDeviceMappingGroup,
  LevelDeviceMappingRow,
  LevelMappedCustomer,
  LevelMappedDevice,
} from "shared/types/integrations";
import { emptyStringHandler, getFullName } from "utilz/helpers";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";
import { LEVEL_SKELETON_ROWS } from "./constants";

export const LevelDeviceMapping = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const mspId = useGlobalStore((state) => state.mspId);
    const [selectedCustomer, setSelectedCustomer] = useState(0);

    // const [isSubmitting, setIsSubmitting] = useState(false);

    const {
      data: devicesData,
      fetchNextPage: fetchNextDevicesPage,
      isFetchingNextPage: isFetchingDevicesNextPage,
      hasNextPage: hasDevicesNextPage,
      isLoading: isDevicesLoading,
    } = useGetLevelDevices(100);
    const {
      data: levelMappedCustomers,
      isLoading: isLevelMappedCustomersLoading,
    } = useGetLevelMappedCustomers();
    const { data: levelMappedDevices, isLoading: isLevelMappedDevicesLoading } =
      useGetLevelMappedDevices();
    const { mutateAsync: mapDevice } = useMapLevelDevices();

    const [transformedCustomerData, setTransformedCustomerData] = useState<
      LevelDeviceMappingGroup[]
    >([]);

    // The rows are built in the effects below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(devicesData?.pages?.some((page) => page?.data?.length)) &&
      Boolean(levelMappedCustomers?.length) &&
      Boolean(levelMappedDevices) &&
      transformedCustomerData.length === 0;
    const [allDevices, setAllDevices] = useState<LevelDevice[]>([]);

    useEffect(() => {
      if (devicesData?.pages) {
        const allDevices = devicesData.pages.flatMap(
          (page) => page?.data || [],
        );
        setAllDevices(allDevices);
      }
    }, [devicesData]);

    useEffect(() => {
      if (allDevices.length > 0 && levelMappedCustomers && levelMappedDevices) {
        createMappingArray();
      }
    }, [allDevices, levelMappedCustomers, levelMappedDevices]);

    // Debounced server-side search — keeps users beyond the first page
    // reachable instead of filtering only the loaded page.
    const [userSearch, setUserSearch] = useDebounce("", 400);
    const { data: customerUsers, isFetching: isCustomerUsersFetching } =
      useGetAllCustomerUsers({
        perPageCount: 2000,
        sortColumn: "first_name",
        sortDirection: "asc",
        ...(transformedCustomerData.length > 0 && {
          customerId:
            transformedCustomerData[selectedCustomer]?.deskday_customer_id,
        }),
        filter: [
          { column: "customer_users.status", operator: "=", value: "true" },
        ],
        searchQuery: userSearch,
      });

    useEffect(() => {
      if (
        allDevices?.length > 0 &&
        levelMappedCustomers &&
        levelMappedDevices
      ) {
        createMappingArray();
      }
    }, [allDevices, levelMappedCustomers, levelMappedDevices]);

    const createMappingArray = async () => {
      const groupedByCustomerID = allDevices?.reduce(
        (acc: Record<string, LevelDeviceMappingRow[]>, device: LevelDevice) => {
          // Ungrouped devices are filed under the MSP itself.
          const groupId = device.group_id ?? mspId ?? "";
          const groupedDevice: LevelDevice = { ...device, group_id: groupId };
          if (!acc[groupId]) {
            acc[groupId] = [];
          }
          const deviceIndex = levelMappedDevices?.findIndex(
            (mappedDevice: LevelMappedDevice) =>
              mappedDevice.device_id === `${device.id}`,
          );
          const data: LevelDeviceMappingRow = {
            device_id: device?.id,
            device_name: device?.hostname,
            deskday_customer_user_id:
              deviceIndex !== -1
                ? levelMappedDevices[deviceIndex].deskday_customer_user_id
                : "",
            deskday_customer_user_name:
              deviceIndex !== -1
                ? levelMappedDevices[deviceIndex].deskday_customer_user_name
                : "",
            meta_data: groupedDevice,
          };
          acc[groupId].push(data);
          return acc;
        },
        {},
      );

      const dataArray: LevelDeviceMappingGroup[] = [];
      levelMappedCustomers?.forEach((customer: LevelMappedCustomer) => {
        dataArray.push({
          mapping_id: customer.mapping_id,
          external_customer_id: customer.external_customer_id,
          // external_customer_name: customer.external_customer_name,
          deskday_customer_id: customer.deskday_customer_id,
          // deskday_customer_name: customer.deskday_customer_name,
          devices:
            groupedByCustomerID &&
            groupedByCustomerID.hasOwnProperty(customer.external_customer_id)
              ? groupedByCustomerID[customer.external_customer_id]
              : [],
        });
      });

      setTransformedCustomerData(dataArray);
    };

    const handleUserChange = ({
      id,
      cellData,
      rowIndex,
    }: {
      id: string;
      cellData: LevelDeviceMappingRow;
      rowIndex: number;
    }) => {
      if (!id) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            "";
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = "";
          return newPrev;
        });
        return;
      }

      const index = customerUsers?.result.findIndex(
        (user: { mapping_id: string }) => user.mapping_id === id,
      );

      if (index !== -1) {
        setTransformedCustomerData((prev) => {
          const fullname = getFullName(
            customerUsers?.result[index].first_name,
            customerUsers?.result[index].middle_name,
            customerUsers?.result[index].last_name,
          );
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            customerUsers?.result[index].mapping_id;
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = fullname;
          return newPrev;
        });
      }
    };

    const handleTransformForSubmit = () => {
      const submitArray: LevelDeviceMappingGroup[] = [];
      transformedCustomerData?.forEach((customer: LevelDeviceMappingGroup) => {
        const devices = customer.devices.filter(
          (device: LevelDeviceMappingRow) =>
            device.deskday_customer_user_id !== "" &&
            device.deskday_customer_user_name !== "",
        );
        if (devices.length > 0) {
          submitArray.push({
            ...customer,
            devices,
          });
        }
      });

      return submitArray;
    };

    const cellBuilders = {
      user_name: (cellData: LevelDeviceMappingRow, index?: number) => {
        return (
          <SelectField
            key={cellData.device_name}
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customerUsers?.result || []}
            isSearchable
            isLoading={isCustomerUsersFetching}
            onSearchValueChange={setUserSearch}
            // Search is served by the backend (`searchQuery`), so `onFilterValue`
            // returns true to stop the client from re-filtering the results.
            onFilterValue={() => true}
            value={cellData.deskday_customer_user_id}
            size="small"
            isResponsive={false}
            labelMappingKey="first_name"
            valueMappingKey="mapping_id"
            searchFieldProps={{
              placeholder: "Search user",
            }}
            menuItemBuilder={({ item, index }) => (
              <Box key={index}>
                {emptyStringHandler(item.first_name)}{" "}
                {emptyStringHandler(item.last_name)}
              </Box>
            )}
            onValueChange={(value) =>
              handleUserChange({
                id: value,
                cellData,
                rowIndex: index || 0,
              })
            }
            isClearable
            // The options list shrinks while a search is active, so fall back to
            // the name held in row state to keep every row's label stable.
            renderValue={(_selected, selectedOption) => {
              return selectedOption ? (
                <>
                  {emptyStringHandler(selectedOption?.first_name)}{" "}
                  {emptyStringHandler(selectedOption?.last_name)}
                </>
              ) : (
                emptyStringHandler(cellData.deskday_customer_user_name)
              );
            }}
          />
        );
      },
    };

    const onSubmit = async () => {
      const data = handleTransformForSubmit();
      if (data.length > 0) {
        try {
          await mapDevice({ device_mapping: data });
          toast.success("Successfully mapped devices");
          return true;
        } catch {
          toast.error("Failed to map devices");
          return false;
        }
      } else {
        return true;
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack
        sx={{
          py: "24px",
          height: "calc(100% - 34px)",
          overflowY: "scroll",
        }}
        divider={
          <Divider
            flexItem
            sx={{
              marginTop: "24px",
            }}
          />
        }
      >
        <Stack sx={{ px: "32px", gap: "16px" }}>
          <Typography variant="h6">Device Mapping</Typography>
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Please map your users with corresponding device names in Level RMM
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          flex={1}
          sx={{
            width: "100%",
            overflow: "clip",
          }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              width: "350px",
            }}
          >
            <Tabs
              orientation="vertical"
              value={selectedCustomer}
              onChange={(_, index) => {
                setSelectedCustomer(index);
              }}
            >
              {levelMappedCustomers?.map((customer: LevelMappedCustomer) => (
                <Tab
                  key={customer.mapping_id}
                  label={customer.external_customer_name}
                />
              ))}
            </Tabs>
          </Box>
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              flex: "1",
              padding: "32px 32px 0px 32px",
            }}
          >
            <DataTable
              isDataLoading={
                isDevicesLoading ||
                isLevelMappedCustomersLoading ||
                isLevelMappedDevicesLoading ||
                isBuildingRows
              }
              headers={TableHeaders}
              data={transformedCustomerData[selectedCustomer]?.devices || []}
              uniqueKey="device_id"
              cellBuilders={cellBuilders}
              selectedItems={[]}
              currentPageNumber={0}
              itemsPerPage={LEVEL_SKELETON_ROWS}
              multiselect={false}
              containerStyles={{ height: "100%" }}
              headerCellStyles={{
                textTransform: "none",
              }}
            />
            {hasDevicesNextPage && (
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                position="absolute"
                bottom={"8px"}
                left={"50%"}
                sx={{
                  transform: "translateX(-50%)",
                  zIndex: 1000,
                }}
              >
                <Button
                  variant="ghost"
                  onClick={() => fetchNextDevicesPage()}
                  disabled={isFetchingDevicesNextPage}
                  loading={isFetchingDevicesNextPage}
                  sx={{
                    textTransform: "none",
                    color: theme.palette.primary.main,
                    zIndex: 1000,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isFetchingDevicesNextPage
                    ? "Loading..."
                    : "Load More Devices"}
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>
    );
  },
);

const TableHeaders = [
  {
    name: "Level RMM Device",
    sortable: false,
    key: "device_name",
  },
  {
    name: "DeskDay User",
    sortable: true,
    key: "user_name",
  },
];
